import { useSelector } from "react-redux";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import useTaskUsage from "../../hooks/tasks/useTaskUsage";
import useSubscribe from "../../hooks/subscription/useSubscribe";

const TaskUsage = ({
  hideSubscribeButton = false,
  onSubscribe,
  subscribing: externalSubscribing,
}) => {
  const { loadingUser } = useSelector((state) => state.user);
  const { subscribe: defaultSubscribe, subscribing: defaultSubscribing } =
    useSubscribe();
  const { tasksUsed, taskLimit } = useTaskUsage();

  const handleSubscribe = onSubscribe || defaultSubscribe;
  const isSubscribing =
    externalSubscribing != null ? externalSubscribing : defaultSubscribing;

  const percentageUsed = Math.min((tasksUsed / taskLimit) * 100, 100);

  const getColor = () => {
    if (percentageUsed >= 90) return "#ab3518";
    if (percentageUsed >= 70) return "#c26229";
    if (percentageUsed >= 50) return "#df9600";
    return "#197b68";
  };

  const color = getColor();
  const remaining = Math.max(taskLimit - tasksUsed, 0);

  return (
    <div className="task-usage">
      <Box className="task-usage__card">
        <Box className="task-usage__left-section">
          <Box className="task-usage__ring-container">
            {/* Background track */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={80}
              thickness={4}
              sx={{ color: "rgba(0,0,0,0.08)", position: "absolute" }}
            />
            {/* Foreground progress */}
            <CircularProgress
              variant="determinate"
              value={loadingUser ? 0 : percentageUsed}
              size={80}
              thickness={4}
              sx={{
                color,
                transition: "all 0.4s ease-in-out",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              className="task-usage__ring-label"
              sx={{ color, fontWeight: 700, fontSize: "1.1rem" }}
            >
              {loadingUser ? "…" : tasksUsed}/{taskLimit}
            </Typography>
          </Box>
          <Box className="task-usage__info">
            <Typography className="task-usage__title">Free tasks</Typography>
            <Typography className="task-usage__subtitle" sx={{ color }}>
              {loadingUser
                ? "Loading…"
                : remaining > 0
                  ? `${remaining} remaining`
                  : "Limit reached"}
            </Typography>
          </Box>
        </Box>
        {!hideSubscribeButton && (
          <Button
            variant="contained"
            className="task-usage__subscribe-button"
            onClick={handleSubscribe}
            disabled={isSubscribing}
            sx={{
              background:
                "linear-gradient(144deg, #318b79, #0b6453 50%, #276580)",
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              padding: "10px 20px",
              borderRadius: "8px",
              fontSize: "0.95rem",
              whiteSpace: "nowrap",
              "&:hover": {
                background:
                  "linear-gradient(144deg, #2a7a6f, #094b47 50%, #1f5573)",
              },
            }}
          >
            {isSubscribing ? "Subscribing…" : "Subscribe"}
          </Button>
        )}
      </Box>
    </div>
  );
};

export default TaskUsage;
